import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import BackButton from '@/components/BackButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Lock, Star, CheckCircle, PaperPlaneTilt, ClipboardText } from '@phosphor-icons/react';
import { useLanguage } from '@/contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = '/branding/logo.png';

export default function SurveyFill() {
  const { id } = useParams();
  const { language, direction } = useLanguage();
  const isAr = language === 'ar';
  const T = (ar, en) => (isAr ? ar : en);
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/my/surveys/${id}`, { withCredentials: true });
        setSurvey(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || T('غير متاح', 'Not available'));
        navigate('/surveys');
      } finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="p-8 text-center">{T('جار التحميل...', 'Loading...')}</div>;
  if (!survey) return null;

  if (survey.already_responded) {
    return (
      <div className="min-h-screen bg-[#F2F0FA] dark:bg-slate-950" dir={direction}>
        <header className="sticky top-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] dark:border-slate-700 py-4">
          <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
            <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
            <BackButton className="text-[#655dc6]" />
          </div>
        </header>
        <main className="max-w-xl mx-auto px-4 py-8">
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle size={48} weight="fill" className="mx-auto text-emerald-500 mb-3" />
              <h2 className="text-xl font-semibold text-[#655dc6] mb-2">
                {T('شكراً لك', 'Thank you')}
              </h2>
              <p className="text-sm text-[#655dc6]/70 mb-4">
                {T('لقد شاركت في هذا الاستبيان مسبقاً.', 'You have already responded.')}
              </p>
              <Button onClick={() => navigate('/surveys')} variant="outline">
                {T('العودة', 'Back')}
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const setAns = (qid, v) => setAnswers((p) => ({ ...p, [qid]: v }));

  const submit = async () => {
    const missing = (survey.questions || []).filter((q) => {
      if (!q.required) return false;
      const v = answers[q.id];
      return v === undefined || v === null || (typeof v === 'string' && !v.trim());
    });
    if (missing.length) {
      return toast.error(T(`يرجى الإجابة على ${missing.length} سؤال مطلوب`, `${missing.length} required questions missing`));
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/my/surveys/${id}/respond`, { answers }, { withCredentials: true });
      toast.success(T('شكراً، تم إرسال إجاباتك', 'Thanks, your answers were submitted'));
      navigate('/surveys');
    } catch (e) {
      toast.error(e.response?.data?.detail || T('فشل الإرسال', 'Submit failed'));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#F2F0FA] dark:bg-slate-950" dir={direction} data-testid="survey-fill-page">
      <header className="sticky top-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] dark:border-slate-700 py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-[#655dc6] flex-1">
              {isAr ? survey.title_ar : (survey.title_en || survey.title_ar)}
            </CardTitle>
            {survey.anonymous && (
              <Badge className="bg-blue-100 text-blue-700 border-0 text-xs">
                <Lock size={10} className="me-0.5" /> {T('إجابة مجهولة', 'Anonymous')}
              </Badge>
            )}
          </div>
          <p className="text-sm text-[#655dc6]/70 mt-1">
            {isAr ? survey.description_ar : survey.description_en}
          </p>
          {survey.anonymous && (
            <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-xs text-blue-700 dark:text-blue-300">
              {T('إجاباتك لن تُنسب إليك بأي شكل. ساهم بصراحة.', 'Your answers are not linked to you. Please be honest.')}
            </div>
          )}
        </CardHeader>
      </Card>

      {/* v1.6.32 — Full confidentiality notice. Separate from the small
          "anonymous" chip above so Quality can reassure employees in a
          clear, legally-worded paragraph when they attach it. */}
      {survey.confidentiality_notice && (
        <Card className="border-s-4 border-blue-500 bg-blue-50/60 dark:bg-blue-900/20" data-testid="confidentiality-banner">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Lock size={28} weight="fill" className="flex-shrink-0 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-blue-900 dark:text-blue-200 mb-1.5">
                  {T('🔒 إشعار السرية', '🔒 Confidentiality Notice')}
                </h3>
                <p className="text-sm text-blue-900 dark:text-blue-100 leading-relaxed">
                  {T(
                    'مشاركتك في هذا الاستبيان سريّة بالكامل. إجاباتك لا تُربط باسمك أو ببياناتك الشخصية بأي شكل، وتُستخدم حصريًا من قبل إدارة الجودة لأغراض التحليل وتحسين بيئة العمل. نُثمّن صراحتك ومشاركتك الصادقة.',
                    'Your participation in this survey is strictly confidential. Your responses are not linked to your name or any personal information in any way, and are used solely by the Quality Department for analysis and workplace improvement purposes. We value your honesty and candid participation.',
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {(survey.questions || []).map((q, idx) => (
        <Card key={q.id} data-testid={`q-${q.id}`}>
          <CardContent className="p-4 space-y-3">
            <Label className="flex items-start gap-2">
              <span className="text-xs text-[#655dc6]/50 font-mono mt-0.5">#{idx + 1}</span>
              <span className="flex-1 font-semibold text-[#655dc6]">
                {isAr ? q.title_ar : (q.title_en || q.title_ar)}
                {q.required && <span className="text-rose-500 ms-1">*</span>}
              </span>
            </Label>
            {q.type === 'single_choice' && (
              <div className="space-y-1.5 ps-6">
                {(q.options || []).map((opt, oi) => (
                  <label key={oi} className="flex items-center gap-2 p-2 rounded hover:bg-[#F8F7FC] dark:hover:bg-slate-800 cursor-pointer">
                    <input
                      type="radio"
                      name={q.id}
                      value={opt}
                      checked={answers[q.id] === opt}
                      onChange={() => setAns(q.id, opt)}
                      className="accent-[#655dc6]"
                      data-testid={`opt-${q.id}-${oi}`}
                    />
                    <span className="text-sm">{opt}</span>
                  </label>
                ))}
              </div>
            )}
            {q.type === 'rating' && (
              <div className="flex items-center gap-1 ps-6" data-testid={`rating-${q.id}`}>
                {Array.from({ length: q.scale || 5 }).map((_, i) => {
                  const v = i + 1;
                  const picked = answers[q.id] >= v;
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setAns(q.id, v)}
                      className="p-1 hover:scale-110 transition"
                      data-testid={`star-${q.id}-${v}`}
                    >
                      <Star
                        size={32}
                        weight={picked ? 'fill' : 'regular'}
                        className={picked ? 'text-amber-400' : 'text-gray-300'}
                      />
                    </button>
                  );
                })}
                {answers[q.id] && (
                  <span className="ms-2 text-sm font-mono text-[#655dc6]">{answers[q.id]} / {q.scale}</span>
                )}
              </div>
            )}
            {q.type === 'text' && (
              <Textarea
                rows={3}
                value={answers[q.id] || ''}
                onChange={(e) => setAns(q.id, e.target.value)}
                placeholder={T('اكتب إجابتك هنا...', 'Type your answer...')}
                className="w-full"
                dir={isAr ? 'rtl' : 'ltr'}
                data-testid={`text-${q.id}`}
              />
            )}
          </CardContent>
        </Card>
      ))}

      <div className="sticky bottom-4 flex justify-center">
        <Button
          onClick={submit}
          disabled={submitting}
          size="lg"
          className="bg-[#655dc6] hover:bg-[#564db5] text-white shadow-xl"
          data-testid="submit-survey"
        >
          <PaperPlaneTilt size={18} weight="bold" className="me-2" />
          {submitting ? T('جار الإرسال...', 'Submitting...') : T('إرسال الإجابات', 'Submit Answers')}
        </Button>
      </div>
      </main>
    </div>
  );
}
